import { useCallback, useEffect, useState } from 'react';
import { formationsApi } from '../../api/resources';
import { _inp } from '../../components/ui/Inputs.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { dateSuisse } from '../../lib/dates';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';
import { PuceStatut } from '../ouvrier/MesFormations.jsx';

/** Les demandes de formation qui attendent la réponse du bureau. */
export function demandesEnAttente(liste) {
  return (liste || []).filter((d) => d && d.statut === 'EN_ATTENTE');
}

/** Un montant en francs : « CHF 1'450.00 ». */
const montant = (n) => DEVISE + ' ' + fmt(n);

/** Le jour seul (JJ.MM.AAAA), même quand le serveur envoie un horodatage. */
function leJour(iso) {
  if (!iso) return '';
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(iso))) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      const deux = (n) => String(n).padStart(2, '0');
      return deux(d.getDate()) + '.' + deux(d.getMonth() + 1) + '.' + d.getFullYear();
    }
  }
  return dateSuisse(iso);
}

/** « Du 12.01.2027 au 14.01.2027 », ou la seule date connue. */
function periode(d) {
  if (d.dateDebut && d.dateFin && d.dateDebut !== d.dateFin) {
    return trf('Du {0} au {1}', dateSuisse(d.dateDebut), dateSuisse(d.dateFin));
  }
  return dateSuisse(d.dateDebut || d.dateFin);
}

/** « 3 jours de travail ». */
const joursDeTravail = (n) =>
  Number(n) === 1 ? tr('1 jour de travail') : trf('{0} jours de travail', n);

const styleLabel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  fontWeight: 700,
  color: C.grey,
  minWidth: 0,
};

const styleChamp = { ..._inp, fontSize: 14, minHeight: 44, boxSizing: 'border-box' };

const styleBloc = {
  background: C.white,
  border: '1px solid ' + C.border,
  borderRadius: 14,
  padding: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const styleTitre = {
  fontSize: 12,
  fontWeight: 800,
  color: C.grey,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
};

const styleLien = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 44,
  fontSize: 13,
  fontWeight: 700,
  color: C.teal,
  textDecoration: 'none',
};

const styleGrosBouton = {
  minHeight: 50,
  borderRadius: 14,
  padding: '12px 14px',
  fontSize: 15,
  fontWeight: 800,
  cursor: 'pointer',
};

/** Une demande en attente : ce qu'il demande, l'accord, Refuser / Accepter. */
function CarteEnAttente({ demande: d, onDecide }) {
  const [societePaie, setSocietePaie] = useState('TOUT');
  const [montantSociete, setMontantSociete] = useState('');
  const [joursCours, setJoursCours] = useState('TRAVAIL');
  const [engagementMois, setEngagementMois] = useState('24');
  const [engagementTexte, setEngagementTexte] = useState(() =>
    tr('Il rembourse au prorata des mois restants.'),
  );
  const [texte, setTexte] = useState('');
  const [refus, setRefus] = useState(false);
  const [raison, setRaison] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const changePaie = (valeur) => {
    setSocietePaie(valeur);
    // L'engagement suit ce que la société paie : rien payé, rien à rembourser.
    if (valeur === 'RIEN') setEngagementMois('');
    else if (!String(engagementMois).trim()) setEngagementMois('24');
  };

  const decide = async (action) => {
    if (envoi) return;
    setErreur('');
    setEnvoi(true);
    try {
      await action();
      await onDecide?.();
    } catch (e) {
      setErreur(e?.message || tr("La réponse n'a pas pu partir. Réessaie."));
    } finally {
      setEnvoi(false);
    }
  };

  const accepte = () => {
    const part = Number(montantSociete);
    if (societePaie === 'PARTIE' && !(part > 0)) {
      setErreur(tr('Écris le montant payé par la société.'));
      return;
    }
    const mois = String(engagementMois).trim();
    decide(() =>
      formationsApi.accepte(d.id, {
        societePaie,
        montantSociete: societePaie === 'PARTIE' ? part : null,
        joursCours,
        engagementMois: mois === '' ? null : Number(mois),
        engagementTexte: engagementTexte.trim() || null,
        texte: texte.trim() || null,
      }),
    );
  };

  const refuse = () => decide(() => formationsApi.refuse(d.id, raison.trim() || undefined));

  return (
    <div
      data-testid="formation-en-attente"
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div style={{ ...styleBloc, gap: 6, fontSize: 14, color: C.ink }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.grey }}>
          {d.employeNom}
          {d.demandeLe ? ' · ' + trf('reçue le {0}', leJour(d.demandeLe)) : ''}
        </div>
        <div style={{ fontSize: 16, fontWeight: 800 }}>{d.intitule}</div>
        {d.organisme && <div>{d.organisme}</div>}
        {(d.dateDebut || d.dateFin || d.jours) && (
          <div>
            {[d.dateDebut || d.dateFin ? periode(d) : '', d.jours ? joursDeTravail(d.jours) : '']
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
        {d.prix != null && d.prix !== '' && (
          <div>{trf('Prix du cours : {0}', montant(d.prix))}</div>
        )}
        {d.motif && <div style={{ color: C.grey }}>« {d.motif} »</div>}
        {d.nomFichier && (
          <a
            href={formationsApi.adresseProgramme(d.id)}
            target="_blank"
            rel="noreferrer"
            style={styleLien}
          >
            {tr('Voir le programme (PDF)')}
          </a>
        )}
      </div>

      <div style={styleTitre}>{tr("L'accord, si tu acceptes")}</div>
      <div style={styleBloc}>
        <label style={styleLabel}>
          {tr('La société paie')}
          <select
            value={societePaie}
            onChange={(e) => changePaie(e.target.value)}
            style={styleChamp}
          >
            <option value="TOUT">
              {d.prix ? trf('Tout le cours ({0})', montant(d.prix)) : tr('Tout le cours')}
            </option>
            <option value="PARTIE">{tr('Une partie')}</option>
            <option value="RIEN">{tr('Rien')}</option>
          </select>
        </label>
        {societePaie === 'PARTIE' && (
          <label style={styleLabel}>
            {tr('Montant payé par la société (CHF)')}
            <input
              type="number"
              min="0"
              step="0.05"
              inputMode="decimal"
              value={montantSociete}
              onChange={(e) => setMontantSociete(e.target.value)}
              style={styleChamp}
            />
          </label>
        )}
        <label style={styleLabel}>
          {tr('Les jours de cours')}
          <select
            value={joursCours}
            onChange={(e) => setJoursCours(e.target.value)}
            style={styleChamp}
          >
            <option value="TRAVAIL">{tr('Payés comme du travail')}</option>
            <option value="VACANCES">{tr('Pris sur les vacances')}</option>
            <option value="NON_PAYES">{tr('Non payés')}</option>
          </select>
        </label>
        <label style={styleLabel}>
          {tr("S'il part avant : mois d'engagement")}
          <input
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={engagementMois}
            onChange={(e) => setEngagementMois(e.target.value)}
            style={styleChamp}
          />
        </label>
        <label style={styleLabel}>
          {tr('Conditions (facultatif)')}
          <input
            value={engagementTexte}
            maxLength={500}
            onChange={(e) => setEngagementTexte(e.target.value)}
            style={styleChamp}
          />
        </label>
        <label style={styleLabel}>
          {tr("Un mot pour l'employé (facultatif)")}
          <input
            value={texte}
            maxLength={1000}
            onChange={(e) => setTexte(e.target.value)}
            style={styleChamp}
          />
        </label>
      </div>

      {refus && (
        <div style={styleBloc}>
          <label style={styleLabel}>
            {tr('Raison du refus (facultatif)')}
            <textarea
              value={raison}
              maxLength={1000}
              rows={3}
              onChange={(e) => setRaison(e.target.value)}
              style={{ ...styleChamp, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </label>
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}
          >
            <button
              type="button"
              onClick={() => setRefus(false)}
              style={{
                ...styleGrosBouton,
                border: '1px solid ' + C.border,
                background: C.white,
                color: C.ink,
              }}
            >
              {tr('Annuler')}
            </button>
            <button
              type="button"
              disabled={envoi}
              onClick={refuse}
              style={{ ...styleGrosBouton, border: 'none', background: C.red, color: C.white }}
            >
              {tr('Confirmer le refus')}
            </button>
          </div>
        </div>
      )}

      {erreur && (
        <div role="alert" style={{ fontSize: 13, color: C.red, fontWeight: 700 }}>
          {erreur}
        </div>
      )}

      {!refus && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          <button
            type="button"
            disabled={envoi}
            onClick={() => {
              setErreur('');
              setRefus(true);
            }}
            style={{
              ...styleGrosBouton,
              border: '1.5px solid ' + C.red,
              background: C.white,
              color: C.red,
            }}
          >
            {tr('Refuser')}
          </button>
          <button
            type="button"
            disabled={envoi}
            onClick={accepte}
            style={{
              ...styleGrosBouton,
              border: 'none',
              background: envoi ? C.muted : C.green,
              color: C.white,
            }}
          >
            {tr('Accepter')}
          </button>
        </div>
      )}

      <div
        style={{
          fontSize: 12,
          color: C.grey,
          lineHeight: 1.5,
          background: C.tealL,
          padding: '10px 12px',
          borderRadius: 12,
        }}
      >
        {tr(
          "En acceptant : sa cloche s'allume, la convention se remplit toute seule, il la signe au doigt, et le PDF signé va dans ses documents et dans son dossier. Les jours s'inscrivent dans son planning.",
        )}
      </div>
    </div>
  );
}

/** Une demande déjà décidée : une ligne compacte avec son statut. */
function LigneDecidee({ demande: d }) {
  return (
    <div
      data-testid="formation-decidee"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '10px 0',
        borderTop: '1px solid ' + C.border,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{d.intitule}</div>
          <div style={{ fontSize: 12, color: C.grey }}>
            {[d.employeNom, d.dateDebut || d.dateFin ? periode(d) : ''].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div>
          <PuceStatut statut={d.statut} />
        </div>
      </div>
      {d.statut === 'ACCEPTEE' &&
        (d.signeLe ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.green }}>
              {trf('Signée le {0}', leJour(d.signeLe))}
            </span>
            <a
              href={formationsApi.adresseConvention(d.id)}
              target="_blank"
              rel="noreferrer"
              style={{ ...styleLien, color: C.green }}
            >
              {tr('Convention signée (PDF)')}
            </a>
          </div>
        ) : (
          <div style={{ fontSize: 12, fontWeight: 700, color: C.amber }}>
            {tr('Pas encore signée')}
          </div>
        ))}
    </div>
  );
}

/**
 * Les demandes de formation des employés, côté bureau : celles qui attendent
 * une réponse d'abord, avec l'accord à fixer, puis celles déjà décidées.
 *
 * @param {object} props
 * @param {() => void} [props.onChange] appelé après chaque décision (badge, cloche)
 * @returns {JSX.Element} la liste des demandes de formation
 */
export function DemandesFormation({ onChange }) {
  const [liste, setListe] = useState(null);
  const [erreur, setErreur] = useState('');

  const charge = useCallback(
    () =>
      Promise.resolve(formationsApi.toutes())
        .then((demandes) => {
          setListe(Array.isArray(demandes) ? demandes : []);
          setErreur('');
        })
        .catch((e) => {
          setListe((avant) => avant || []);
          setErreur(e?.message || tr('Impossible de charger les demandes de formation.'));
        }),
    [],
  );

  useEffect(() => {
    charge();
  }, [charge]);

  const apresDecision = async () => {
    await charge();
    onChange?.();
  };

  if (liste === null) {
    return <div style={{ fontSize: 13, color: C.grey, padding: 12 }}>{tr('Chargement…')}</div>;
  }

  const enAttente = demandesEnAttente(liste);
  const decidees = liste.filter((d) => d && d.statut !== 'EN_ATTENTE');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {erreur && (
        <div role="alert" style={{ fontSize: 13, color: C.red, fontWeight: 700 }}>
          {erreur}
        </div>
      )}
      {liste.length === 0 && !erreur && (
        <div style={{ ...styleBloc, fontSize: 13, color: C.grey }}>
          {tr("Aucune demande de formation pour l'instant.")}
        </div>
      )}
      {enAttente.map((d) => (
        <CarteEnAttente key={d.id} demande={d} onDecide={apresDecision} />
      ))}
      {decidees.length > 0 && (
        <div style={{ ...styleBloc, gap: 0 }}>
          <div style={{ ...styleTitre, marginBottom: 6 }}>{tr('Déjà décidées')}</div>
          {decidees.map((d) => (
            <LigneDecidee key={d.id} demande={d} />
          ))}
        </div>
      )}
    </div>
  );
}
